import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import EngagementModal from '../components/brand/EngagementModal';
import {
  LayoutDashboard, Search, Handshake, MessageSquare, Building2,
  MapPin, Star, Loader2, Clock, Briefcase, Send, ArrowUpRight, Filter,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LICENSE_TYPES = ['Any', 'RN', 'LPN', 'CNA', 'NP', 'MD', 'DO', 'PA', 'MSW', 'PT', 'OT', 'ST', 'Chaplain'];

export default function AgencyFindClinicians() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clinicians, setClinicians] = useState([]);
  const [workType, setWorkType] = useState('any');
  const [licenseType, setLicenseType] = useState('Any');
  const [specialty, setSpecialty] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const [engageWith, setEngageWith] = useState(null);

  const fetchClinicians = async () => {
    setLoading(true);
    try {
      const params = { agency_work: true };
      if (workType !== 'any') params.work_type = workType;
      if (licenseType !== 'Any') params.license_type = licenseType;
      if (specialty) params.specialty = specialty;
      if (state) params.state = state;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/public/clinicians`, { params });
      setClinicians(data);
    } catch (e) {
      setClinicians([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchClinicians(); }, []);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/agency/dashboard' },
    { icon: Search, label: 'Find clinicians', href: '/agency/find-clinicians', active: true },
    { icon: Handshake, label: 'Engagements', href: '/agency/dashboard#engagements' },
    { icon: MessageSquare, label: 'Messages', href: '/agency/messages' },
    { icon: Building2, label: 'Organization', href: '/agency/dashboard#profile' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Find clinicians"
      subtitle="Search verified independent clinicians open to temporary or permanent work."
      testId="agency-find-clinicians"
    >
      {/* Filters */}
      <div className="cn-card p-5 mb-6" data-testid="agency-search-filters">
        <div className="flex items-center gap-2 mb-4 text-cn-text-muted text-sm font-semibold"><Filter className="w-4 h-4" /> Filters</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchClinicians()} placeholder="Search name, skills, city…" className="h-11 rounded-xl border-cn-border" data-testid="agency-search-q" />
          </div>
          <Select value={workType} onValueChange={setWorkType}>
            <SelectTrigger className="h-11 rounded-xl border-cn-border" data-testid="agency-filter-worktype"><SelectValue placeholder="Work type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any work type</SelectItem>
              <SelectItem value="temporary">Temporary / Locum</SelectItem>
              <SelectItem value="permanent">Permanent</SelectItem>
            </SelectContent>
          </Select>
          <Select value={licenseType} onValueChange={setLicenseType}>
            <SelectTrigger className="h-11 rounded-xl border-cn-border" data-testid="agency-filter-license"><SelectValue placeholder="License" /></SelectTrigger>
            <SelectContent>
              {LICENSE_TYPES.map((l) => <SelectItem key={l} value={l}>{l === 'Any' ? 'Any license' : l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" maxLength={2} className="h-11 rounded-xl border-cn-border uppercase" data-testid="agency-filter-state" />
            <Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="Specialty" className="h-11 rounded-xl border-cn-border" data-testid="agency-filter-specialty" />
          </div>
        </div>
        <button onClick={fetchClinicians} className="cn-btn cn-btn-primary mt-4 text-sm" data-testid="agency-search-btn">
          <Search className="w-4 h-4" /> Search clinicians
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-cn-primary animate-spin" /></div>
      ) : clinicians.length === 0 ? (
        <div className="cn-card p-12 text-center">
          <Building2 className="w-12 h-12 text-cn-text-muted mx-auto mb-3" />
          <h3 className="cn-display text-xl text-cn-text">No clinicians match yet</h3>
          <p className="text-sm text-cn-text-muted mt-1">Try widening your filters. Only clinicians who opted into agency work appear here.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-cn-text-muted mb-4">{clinicians.length} clinician{clinicians.length === 1 ? '' : 's'} open to agency work</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {clinicians.map((c) => (
              <div key={c.id} className="cn-card cn-card-hover p-5 flex flex-col" data-testid={`agency-clinician-${c.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl overflow-hidden bg-cn-sage-100 flex items-center justify-center flex-shrink-0 text-cn-primary font-bold">
                    {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> : (c.name?.[0] || 'C')}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-cn-text leading-snug truncate">{c.name}</h3>
                    <p className="text-xs text-cn-text-muted">{c.license_type}{c.years_of_experience ? ` · ${c.years_of_experience} yrs` : ''}</p>
                    <p className="text-xs text-cn-text-muted flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {[c.city, c.state].filter(Boolean).join(', ') || '—'}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="font-display font-bold text-cn-text">${c.hourly_rate}<span className="text-[11px] text-cn-text-muted font-normal">/hr</span></div>
                    {c.review_count > 0 && <div className="text-[11px] text-cn-text-muted inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-cn-accent fill-cn-accent" /> {c.rating}</div>}
                  </div>
                </div>

                {c.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.specialties.slice(0, 3).map((s) => <span key={s} className="text-[11px] bg-cn-bone-200 text-cn-text px-2 py-0.5 rounded-full">{s}</span>)}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(c.agency_work_types || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-[11px] font-semibold text-cn-primary bg-cn-sage-100 px-2 py-0.5 rounded-full capitalize">
                      {t === 'permanent' ? <Briefcase className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{t}
                    </span>
                  ))}
                </div>
                {c.agency_availability && <p className="mt-2 text-xs text-cn-text-muted italic">"{c.agency_availability}"</p>}

                <div className="mt-auto pt-4 flex gap-2">
                  <button onClick={() => setEngageWith(c)} className="cn-btn cn-btn-primary text-sm flex-1 !py-2" data-testid={`agency-engage-${c.id}`}>
                    <Send className="w-3.5 h-3.5" /> Engage
                  </button>
                  <a href={`/clinicians/${c.id}`} target="_blank" rel="noreferrer" className="cn-btn cn-btn-ghost text-sm !py-2" data-testid={`agency-view-${c.id}`}>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <EngagementModal
        open={!!engageWith}
        clinician={engageWith}
        getAuthHeader={getAuthHeader}
        onClose={() => setEngageWith(null)}
      />
    </DashboardShell>
  );
}
